import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PayableSettlement,
  PERMISSIONS,
  SupplierPaymentStatus,
  type PayableInvoicePayment,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { usePayableInvoiceQuery } from '../api/payables.api';
import {
  DueStateBadge,
  InvoiceDocumentBadge,
  SettlementBadge,
  SupplierPaymentStatusBadge,
} from '../components/PayableBadges';

function Detail({ label, value, ltr = false }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm" dir={ltr ? 'ltr' : 'auto'}>
        {value}
      </dd>
    </div>
  );
}

/**
 * One invoice, as finance reads it.
 *
 * Owned by payables rather than purchasing so that someone who settles invoices does not need
 * purchasing capabilities to do their job. It shows what is owed and what has been paid, and stops
 * there: the deliveries and the costs behind the document are purchasing's page, one link away.
 *
 * A voided payment keeps its row here and settles nothing, so the history of what was tried is
 * never lost — and the row says as much in words, not by being greyer.
 */
export function PayableInvoiceDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { can } = usePermissions();
  const { branches } = useBranch();
  const { format } = useCurrency();

  const { data, isPending, isError, error, refetch } = usePayableInvoiceQuery(invoiceId);

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('payables.invoices.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const invoice = data;
  const isPosted = invoice.status !== 'VOIDED';
  // Paying needs the capability, something still owed, a live document, and a branch this user can reach.
  const canPay =
    can(PERMISSIONS.PAYABLES_PAY) &&
    isPosted &&
    invoice.outstandingMinor > 0 &&
    branches.some((branch) => branch.id === invoice.branchId);

  const paymentColumns: Column<PayableInvoicePayment>[] = [
    {
      key: 'reference',
      header: t('payables.fields.paymentReference'),
      primary: true,
      render: (payment) => (
        <span className="text-ink font-mono font-medium" dir="ltr">
          {payment.reference}
        </span>
      ),
    },
    {
      key: 'paymentDate',
      header: t('payables.fields.paymentDate'),
      render: (payment) => (
        <span className="text-ink text-sm" dir="ltr">
          {payment.paymentDate}
        </span>
      ),
    },
    {
      key: 'method',
      header: t('payables.fields.method'),
      render: (payment) => (
        <span className="text-muted text-sm">{t(`payables.methods.${payment.method}`)}</span>
      ),
    },
    {
      key: 'allocated',
      header: t('payables.fields.allocatedAmount'),
      align: 'end',
      render: (payment) => (
        <span className="flex flex-col items-end">
          <span
            className={
              payment.status === SupplierPaymentStatus.VOIDED
                ? 'text-muted line-through'
                : 'text-ink font-medium'
            }
            dir="ltr"
          >
            {format(payment.allocatedMinor)}
          </span>
          {payment.status === SupplierPaymentStatus.VOIDED ? (
            <span className="text-muted text-xs">{t('payables.settlesNothing')}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('payables.fields.paymentStatus'),
      render: (payment) => <SupplierPaymentStatusBadge status={payment.status} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (payment) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('payables.openPaymentNamed', { reference: payment.reference })}
          onClick={() => void navigate(`/finance/payables/payments/${payment.supplierPaymentId}`)}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={invoice.reference}
        subtitle={t('payables.invoices.detailSubtitle', {
          supplier: invoice.supplierName,
          number: invoice.supplierInvoiceNumber,
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/finance/payables/invoices')}>
              {t('payables.backToInvoices')}
            </Button>
            {canPay ? (
              <Button
                onClick={() =>
                  void navigate(
                    `/finance/payables/payments/new?invoiceId=${invoice.purchaseInvoiceId}`,
                  )
                }
              >
                {t('payables.recordPayment')}
              </Button>
            ) : null}
          </div>
        }
      />

      {!isPosted ? <Alert tone="warning">{t('payables.invoices.voidedNotice')}</Alert> : null}

      <Card>
        <CardHeader
          title={t('payables.invoices.summarySection')}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <InvoiceDocumentBadge status={invoice.status} />
              <SettlementBadge settlement={invoice.settlement} />
              <DueStateBadge invoice={invoice} />
            </div>
          }
          wrapAction
        />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail label={t('payables.fields.supplier')} value={invoice.supplierName} />
            <Detail label={t('payables.fields.branch')} value={invoice.branchName} />
            <Detail
              label={t('payables.fields.supplierInvoiceNumber')}
              value={invoice.supplierInvoiceNumber}
              ltr
            />
            <Detail label={t('payables.fields.currency')} value={invoice.currencyCode} ltr />
            <Detail label={t('payables.fields.invoiceDate')} value={invoice.invoiceDate} ltr />
            <Detail label={t('payables.fields.dueDate')} value={invoice.dueDate} ltr />
            <Detail
              label={t('payables.fields.ageing')}
              value={
                invoice.ageingBucket
                  ? t(`payables.buckets.${invoice.ageingBucket}`)
                  : t('payables.notAged')
              }
            />
            <Detail
              label={t('payables.fields.dueState')}
              value={
                invoice.daysOverdue
                  ? t('payables.due.overdueBy', { count: invoice.daysOverdue })
                  : invoice.daysUntilDue
                    ? t('payables.due.inDays', { count: invoice.daysUntilDue })
                    : t(`payables.due.${invoice.due}`)
              }
            />
          </dl>
        </CardBody>
        <CardBody className="border-line space-y-2 border-t text-sm">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('payables.fields.invoiceTotal')}</span>
            <span className="text-ink" dir="ltr">
              {format(invoice.obligationMinor)}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('payables.fields.allocated')}</span>
            <span className="text-ink" dir="ltr">
              {format(invoice.activeAllocatedMinor)}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-ink font-medium">{t('payables.fields.outstanding')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {format(invoice.outstandingMinor)}
            </span>
          </div>
          {invoice.settlement === PayableSettlement.PARTIALLY_PAID ? (
            <p className="text-muted text-xs">{t('payables.partiallyPaidHint')}</p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('payables.paymentHistoryTitle')}
          subtitle={t('payables.paymentHistoryHint')}
        />
        <DataTable
          caption={t('payables.paymentHistoryCaption')}
          columns={paymentColumns}
          rows={invoice.payments}
          rowKey={(payment) => payment.supplierPaymentId}
          emptyTitle={t('payables.noPaymentsYet')}
          emptyDescription={t('payables.noPaymentsYetHint')}
        />
      </Card>

      <p className="text-muted text-xs">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/invoices/${invoice.purchaseInvoiceId}`)}
        >
          {t('payables.openInPurchasing')}
        </Button>
      </p>
    </div>
  );
}
